import { useEffect, useRef, useState } from 'react';
import { CloudArrowUpIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface Props {
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  sessionEmail?: string;
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

const FileUploadCard = ({ onUpload, isUploading, sessionEmail }: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const dragDepth = useRef(0);

  const isAccepted = (file: File) => {
    if (ACCEPTED_TYPES.includes(file.type)) return true;
    const name = file.name?.toLowerCase() ?? '';
    return ['.png', '.jpg', '.jpeg', '.webp'].some((ext) => name.endsWith(ext));
  };

  const startUpload = async (file: File) => {
    setLocalError(null);
    try {
      await onUpload(file);
    } catch {
      setLocalError('We could not start the analysis. Please try again.');
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const validateAndSetFile = (file: File | null | undefined) => {
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (!isAccepted(file)) {
      setLocalError('Please upload an image file (PNG, JPG, WEBP).');
      setSelectedFile(null);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setLocalError('Files must be 10MB or smaller.');
      setSelectedFile(null);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }
    setLocalError(null);
    setSelectedFile(file);
    void startUpload(file);
  };

  const triggerSelect = () => {
    if (isUploading) return;
    inputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    validateAndSetFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = 0;
    setIsDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    validateAndSetFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current += 1;
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragActive(false);
  };

  // Prevent the browser from navigating away if user drops outside the zone while active
  useEffect(() => {
    if (!isDragActive) return;
    const prevent = (e: DragEvent) => {
      e.preventDefault();
    };
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, [isDragActive]);

  return (
    <div className="theme-card p-8 text-center bg-white">

      <div
        className={clsx(
          'mt-8 flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed px-6 py-12 text-center transition',
          (selectedFile || isDragActive) ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white'
        )}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={[
            '.png',
            '.jpg',
            '.jpeg',
            '.webp',
            ...ACCEPTED_TYPES
          ].join(',')}
          onChange={handleFileChange}
          className="hidden"
        />
        <DocumentArrowUpIcon className="h-12 w-12 text-fastbar.teal" aria-hidden />
        <div>
          {selectedFile ? (
            <p className="text-sm text-slate-600">
              <strong>{selectedFile.name}</strong> - {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
            </p>
          ) : (
            <p className="text-sm text-slate-600">Drop your file here or browse to upload</p>
          )}
          <p className="mt-1 text-xs text-slate-900/60">PNG, JPG, WEBP - max 10MB</p>
          {sessionEmail && (
            <p className="mt-2 text-xs text-slate-900/60">Session for {sessionEmail}</p>
          )}
        </div>

        <button
          type="button"
          onClick={triggerSelect}
          disabled={isUploading}
          className="btn-secondary mt-2"
        >
          Browse files
        </button>
      </div>

      {selectedFile && !localError && (
        <div className="mt-6 flex justify-center text-sm text-slate-600">
          {isUploading ? (
            <span className="inline-flex items-center gap-2">
              <CloudArrowUpIcon className="h-4 w-4 animate-pulse" aria-hidden />
              Uploading and analyzing your menu...
            </span>
          ) : (
            <span>Ready to retry</span>
          )}
        </div>
      )}

      {localError && (
        <div className="mt-6 flex flex-col items-center gap-3 text-sm text-red-600">
          <p>{localError}</p>
          {selectedFile && (
            <button
              type="button"
              onClick={() => void startUpload(selectedFile)}
              className="btn-primary px-4 py-2 text-xs"
              disabled={isUploading}
            >
              Try again
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUploadCard;
